import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './WorkshopSessionDetail.css';

function WorkshopSessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchSessionDetail();
  }, [id]);

  const fetchSessionDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/workshops/sessions/${id}`);
      setSession(response.data.session);
      setAttendees(response.data.bookings || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch session detail:', err);
      setError('Failed to load session details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const handleCheckIn = async (bookingId) => {
    try {
      await axios.put(`/api/workshops/bookings/${bookingId}/check-in`);
      setAttendees((prev) =>
        prev.map((a) =>
          a.id === bookingId ? { ...a, status: 'checked_in', checked_in_at: new Date().toISOString() } : a
        )
      );
    } catch (err) {
      console.error('Failed to check in:', err);
      alert('Failed to check in attendee');
    }
  };

  const handleCheckInAll = async () => {
    try {
      setActionLoading(true);
      const pending = attendees.filter((a) => a.status === 'confirmed');
      await Promise.all(
        pending.map((a) => axios.put(`/api/workshops/bookings/${a.id}/check-in`))
      );
      setAttendees((prev) =>
        prev.map((a) =>
          a.status === 'confirmed'
            ? { ...a, status: 'checked_in', checked_in_at: new Date().toISOString() }
            : a
        )
      );
    } catch (err) {
      console.error('Failed to check in all:', err);
      alert('Failed to check in all attendees');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSession = async () => {
    try {
      setActionLoading(true);
      await axios.put(`/api/workshops/sessions/${id}`, { status: 'cancelled' });
      setSession((prev) => ({ ...prev, status: 'cancelled' }));
      setShowCancelConfirm(false);
    } catch (err) {
      console.error('Failed to cancel session:', err);
      alert('Failed to cancel session');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrintManifest = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="workshop-session-detail">
        <div className="ws-loading">
          <div className="spinner"></div>
          <p>Loading session details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workshop-session-detail">
        <div className="ws-error">{error}</div>
        <button className="ws-btn-secondary" onClick={() => navigate('/workshops/sessions')}>
          Back to Sessions
        </button>
      </div>
    );
  }

  if (!session) return null;

  const bookedCount = attendees.reduce((sum, a) => sum + (a.attendee_count || 1), 0);
  const capacityPercent = session.capacity ? Math.min(100, Math.round((bookedCount / session.capacity) * 100)) : 0;
  const checkedInCount = attendees.filter((a) => a.status === 'checked_in').length;
  const pendingCheckIn = attendees.filter((a) => a.status === 'confirmed').length;

  return (
    <div className="workshop-session-detail">
      <div className="ws-page-header">
        <button className="ws-btn-secondary" onClick={() => navigate('/workshops/sessions')}>
          Back to Sessions
        </button>
        <h1>Session Detail</h1>
        <div className="ws-header-actions">
          <button className="ws-btn-secondary" onClick={handlePrintManifest}>
            Print Manifest
          </button>
          <button className="ws-btn-primary" onClick={fetchSessionDetail}>
            Refresh
          </button>
        </div>
      </div>

      <div className="ws-detail-info">
        <div className="ws-detail-grid">
          <div className="ws-detail-item">
            <span className="ws-detail-label">Date</span>
            <span className="ws-detail-value">{formatDate(session.session_date)}</span>
          </div>
          <div className="ws-detail-item">
            <span className="ws-detail-label">Time</span>
            <span className="ws-detail-value">{formatTime(session.start_time)} - {formatTime(session.end_time)}</span>
          </div>
          <div className="ws-detail-item">
            <span className="ws-detail-label">Location</span>
            <span className="ws-detail-value">{session.location_name || session.location_slug}</span>
          </div>
          <div className="ws-detail-item">
            <span className="ws-detail-label">Type</span>
            <span className="ws-detail-value">{session.workshop_type_name || 'Workshop'}</span>
          </div>
          <div className="ws-detail-item">
            <span className="ws-detail-label">Status</span>
            <span className={`ws-badge ws-badge-${session.status}`}>{session.status}</span>
          </div>
        </div>
      </div>

      <div className="ws-section">
        <h2>Capacity</h2>
        <div className="ws-capacity-large">
          <div className="ws-capacity-header">
            <span>{bookedCount} / {session.capacity} booked</span>
            <span>{capacityPercent}%</span>
          </div>
          <div className="ws-capacity-bar-large">
            <div
              className="ws-capacity-fill-large"
              style={{
                width: `${capacityPercent}%`,
                backgroundColor: capacityPercent >= 100 ? '#e65100' : '#8B6F4E'
              }}
            ></div>
          </div>
          <div className="ws-capacity-stats">
            <span>Checked in: {checkedInCount}</span>
            <span>Pending: {pendingCheckIn}</span>
          </div>
        </div>
      </div>

      <div className="ws-section">
        <div className="ws-section-header">
          <h2>Attendees</h2>
          <div className="ws-section-actions">
            <button
              className="ws-btn-primary ws-btn-sm"
              onClick={() => navigate(`/workshops/bookings/new?session_id=${id}`)}
            >
              Add Booking
            </button>
            {pendingCheckIn > 0 && (
              <button
                className="ws-btn-secondary ws-btn-sm"
                onClick={handleCheckInAll}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Check In All'}
              </button>
            )}
          </div>
        </div>

        {attendees.length === 0 ? (
          <div className="ws-empty">No bookings for this session yet</div>
        ) : (
          <div className="ws-table-container">
            <table className="ws-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Attendees</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {attendees.map((booking) => (
                  <tr key={booking.id}>
                    <td className="ws-text-bold">{booking.customer_name}</td>
                    <td>{booking.customer_email}</td>
                    <td>{booking.customer_phone || '--'}</td>
                    <td>{booking.attendee_count || 1}</td>
                    <td>
                      <span className={`ws-badge ws-badge-${booking.status}`}>
                        {booking.status}
                      </span>
                    </td>
                    <td>
                      {booking.status === 'confirmed' && (
                        <button
                          className="ws-btn-primary ws-btn-sm"
                          onClick={() => handleCheckIn(booking.id)}
                        >
                          Check In
                        </button>
                      )}
                      {booking.status === 'checked_in' && (
                        <span className="ws-checked-in-text">Checked in</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {session.status !== 'cancelled' && session.status !== 'completed' && (
        <div className="ws-section ws-danger-section">
          {showCancelConfirm ? (
            <div className="ws-confirm-box">
              <p>Are you sure you want to cancel this session? This will notify all booked attendees.</p>
              <div className="ws-confirm-actions">
                <button
                  className="ws-btn-danger"
                  onClick={handleCancelSession}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Cancelling...' : 'Yes, Cancel Session'}
                </button>
                <button className="ws-btn-secondary" onClick={() => setShowCancelConfirm(false)}>
                  No, Keep Session
                </button>
              </div>
            </div>
          ) : (
            <button className="ws-btn-danger" onClick={() => setShowCancelConfirm(true)}>
              Cancel Session
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default WorkshopSessionDetail;
